import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { formatBRL } from "@/lib/format";
import type { Product } from "@/lib/products";
import { ShoppingCart, Sparkles, Heart } from "lucide-react";
import { addToWishlist, removeFromWishlist, myWishlistQuery } from "@/lib/wishlist.functions";

export function ProductCard({ product }: { product: Product }) {
  const addItem = useCart((s) => s.addItem);
  const openDrawer = useCart((s) => s.openDrawer);
  const user = useAuth((s) => s.user);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: wishlist } = useQuery({ ...myWishlistQuery(), enabled: !!user });
  const isWishlisted = !!wishlist?.some((p) => p.id === product.id);

  const toggleWishlist = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!user) {
      navigate({ to: "/login" });
      return;
    }
    if (isWishlisted) {
      await removeFromWishlist({ data: product.id });
    } else {
      await addToWishlist({ data: product.id });
    }
    queryClient.invalidateQueries({ queryKey: ["my-wishlist"] });
  };

  const outOfStock = !product.is_custom_quote && product.stock_quantity <= 0;

  return (
    <div className="group flex flex-col overflow-hidden rounded-lg bg-card shadow-sm hover:shadow-lg transition-shadow border border-border">
      <Link
        to="/produto/$slug"
        params={{ slug: product.slug }}
        className="relative block aspect-[4/3] overflow-hidden bg-muted"
      >
        {product.images[0] && (
          <img
            src={product.images[0]}
            alt={product.name}
            loading="lazy"
            className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        )}
        {product.category && (
          <span className="absolute top-2 left-2 rounded bg-black/60 px-2 py-0.5 text-xs font-medium text-white backdrop-blur-sm">
            {product.category.name}
          </span>
        )}
        {product.is_featured && (
          <span className="absolute top-2 right-2 flex items-center gap-1 rounded bg-eg-gold px-2 py-0.5 text-xs font-bold text-eg-dark">
            <Sparkles className="h-3 w-3" /> Destaque
          </span>
        )}
        {outOfStock && (
          <span className="absolute bottom-2 left-2 rounded bg-red-600 px-2 py-0.5 text-xs font-bold text-white">
            Esgotado
          </span>
        )}
        <button
          type="button"
          onClick={toggleWishlist}
          aria-label={isWishlisted ? "Remover dos favoritos" : "Adicionar aos favoritos"}
          className="absolute bottom-2 right-2 rounded-full bg-white/90 p-1.5 shadow hover:bg-white transition-colors"
        >
          <Heart
            className={`h-4 w-4 ${isWishlisted ? "fill-red-500 text-red-500" : "text-muted-foreground"}`}
          />
        </button>
      </Link>

      <div className="flex flex-1 flex-col p-4">
        <h2 className="font-display text-sm font-semibold sm:text-base">
          <Link
            to="/produto/$slug"
            params={{ slug: product.slug }}
            className="text-foreground hover:text-eg-blue line-clamp-2"
          >
            {product.name}
          </Link>
        </h2>
        {product.short_description && (
          <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
            {product.short_description}
          </p>
        )}

        <div className="mt-3 flex-1">
          {product.is_custom_quote ? (
            <p className="text-sm font-semibold text-eg-blue">Orçamento sob medida</p>
          ) : (
            <>
              {product.compare_at_price &&
                product.price &&
                product.compare_at_price > product.price && (
                  <p className="text-xs text-muted-foreground line-through">
                    {formatBRL(product.compare_at_price)}
                  </p>
                )}
              <p className="text-lg font-bold text-eg-dark">{formatBRL(product.price)}</p>
            </>
          )}
        </div>

        <div className="mt-3">
          {product.is_custom_quote ? (
            <Link
              to="/produto/$slug"
              params={{ slug: product.slug }}
              className="block w-full rounded-md bg-eg-gold px-3 py-2 text-center text-sm font-semibold text-eg-dark hover:bg-eg-goldhover transition-colors"
            >
              Solicitar Orçamento
            </Link>
          ) : (
            <button
              type="button"
              disabled={outOfStock}
              onClick={() => {
                addItem(product, 1);
                openDrawer();
              }}
              className="flex w-full items-center justify-center gap-2 rounded-md bg-eg-dark px-3 py-2 text-sm font-semibold text-white hover:bg-eg-gold hover:text-eg-dark disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground transition-colors"
            >
              <ShoppingCart className="h-4 w-4" />
              {outOfStock ? "Esgotado" : "Adicionar ao Carrinho"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
